import { useQuery } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import { fetchIntakeConfig } from './api';
import type { IntakeFieldConfig } from './types';

/**
 * Falls back to this fixed set if the fetch fails, or the hospital has not
 * configured intake_form_config yet (a genuinely empty table on a fresh
 * tenant) — the same default field list the legacy RegisterStage.jsx shipped
 * with, reproduced exactly so a hospital with no admin customisation sees
 * an identical Register screen to before.
 */
const DEFAULT_FIELDS: IntakeFieldConfig[] = [
  { id: -1, field_name: 'name', display_label: 'Patient name', field_type: 'text', status: 'mandatory', display_order: 1, options: null },
  { id: -2, field_name: 'mobile', display_label: 'Mobile number', field_type: 'tel', status: 'mandatory', display_order: 2, options: null },
  { id: -3, field_name: 'gender', display_label: 'Gender', field_type: 'select', status: 'mandatory', display_order: 3, options: null },
  { id: -4, field_name: 'age', display_label: 'Age (years)', field_type: 'number', status: 'optional', display_order: 4, options: null },
  { id: -5, field_name: 'dob', display_label: 'Date of birth', field_type: 'date', status: 'optional', display_order: 5, options: null },
  { id: -6, field_name: 'email', display_label: 'Email', field_type: 'email', status: 'optional', display_order: 6, options: null },
  { id: -7, field_name: 'address', display_label: 'Address', field_type: 'text', status: 'optional', display_order: 7, options: null },
  { id: -8, field_name: 'abha_number', display_label: 'ABHA number', field_type: 'text', status: 'optional', display_order: 8, options: null },
];

export function useIntakeConfig() {
  return useQuery({
    queryKey: keys.config.intakeConfig(),
    queryFn: fetchIntakeConfig,
    select: (data) => {
      const fields = Array.isArray(data) && data.length ? data : DEFAULT_FIELDS;
      return fields.filter((f) => f.status !== 'hidden').sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    },
    // Admin-configured, changes rarely — worth a longer stale window than
    // the app default so Register does not re-fetch on every stage visit
    // within a session.
    staleTime: 5 * 60_000,
  });
}

/** Shipped choices — what migration_patient_title.sql seeds, used only if the row has none. */
export const DEFAULT_TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Ms.', 'Miss'] as const;

export interface TitleFieldConfig {
  /** 'hidden' when the admin has switched Title off, or the row does not exist yet (tenant not migrated). */
  status: IntakeFieldConfig['status'];
  options: string[];
}

/**
 * The Title dropdown's settings (Admin → Clinical → Intake Form). Same cached
 * query as useIntakeConfig, but read unfiltered: Edit record still needs the
 * options when Title is hidden, to show a title a patient already has.
 */
export function useTitleField() {
  return useQuery({
    queryKey: keys.config.intakeConfig(),
    queryFn: fetchIntakeConfig,
    select: (data): TitleFieldConfig => {
      const row = Array.isArray(data) ? data.find((f) => f.field_name === 'title') : undefined;
      if (!row) return { status: 'hidden', options: [...DEFAULT_TITLE_OPTIONS] };
      const options = Array.isArray(row.options)
        ? row.options.filter((o): o is string => typeof o === 'string' && o.trim() !== '')
        : [];
      return { status: row.status, options: options.length ? options : [...DEFAULT_TITLE_OPTIONS] };
    },
    staleTime: 5 * 60_000,
  });
}
